/**
 * Shares API client
 *
 * Phase 4-4 / Stream 4 / Plan §12 / Design §5.8 C6-min 分享链召回.
 *
 * 写动作 (revoke) 通过 client.ts 自动注入 Idempotency-Key。
 *
 * revoke 两段式 "先通知后作废"：
 *  - immediate=false (默认)：stage 1 立即 flip pending_revoke +
 *    自动触发 share-revoked notify；24h 后 cron flip 到 revoked
 *  - immediate=true：立即 flip revoked + 通知（紧急违规内容用）
 */
import { apiClient } from './client';

export type ShareStatus = 'active' | 'pending_revoke' | 'revoked';

export interface ShareDto {
  shareId: string;
  userId: string;
  workId: string;
  chapterId: string;
  status: ShareStatus;
  shareToken: string;
  createdAt: number;
  updatedAt: number;
  createdBy: string;
  updatedBy: string;
  lastActorId: string | null;
  revokeScheduledAt: number | null;
  revokeReason: string | null;
}

export interface ListSharesFilter {
  status?: ShareStatus;
  userId?: string;
  workId?: string;
  chapterId?: string;
}

export interface ListSharesResponse {
  items: ShareDto[];
  total: number;
}

export interface RevokeShareResponse {
  shareId: string;
  status: 'pending_revoke' | 'revoked';
  revokeScheduledAt?: number;
}

interface ServiceResponse<T> {
  code: number;
  message: string;
  data: T;
  requestId: string;
}

/**
 * 列表 (强制 workId/userId/chapterId 至少一个；前端 form 已强制)。
 */
export async function listShares(filter: ListSharesFilter): Promise<ListSharesResponse> {
  const params: Record<string, string> = {};
  if (filter.status) params.status = filter.status;
  if (filter.userId) params.userId = filter.userId;
  if (filter.workId) params.workId = filter.workId;
  if (filter.chapterId) params.chapterId = filter.chapterId;
  const resp = await apiClient.get<ServiceResponse<ListSharesResponse>>('/shares', { params });
  return resp.data.data;
}

export async function getShare(shareId: string): Promise<ShareDto> {
  const resp = await apiClient.get<ServiceResponse<ShareDto>>(
    `/shares/${encodeURIComponent(shareId)}`,
  );
  return resp.data.data;
}

export interface RevokeShareInput {
  reason: string;
  /** 默认 false (先通知 24h 后作废)；true = 立即作废（仅紧急违规） */
  immediate?: boolean;
}

export async function revokeShare(
  shareId: string, input: RevokeShareInput,
): Promise<RevokeShareResponse> {
  const resp = await apiClient.post<ServiceResponse<RevokeShareResponse>>(
    `/shares/${encodeURIComponent(shareId)}/revoke`,
    input,
  );
  return resp.data.data;
}
